import { ArrowUp, Linkedin, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { marTerms, marDiscountPct, formatUsd } from "./terms";
import ritaImg from "@/assets/rita-galmarini.png";
import juanPabloImg from "@/assets/juan-pablo-barrios.png";

const founders = [
  {
    name: "Rita Galmarini",
    role: "Co-fundadora · Esencia",
    image: ritaImg,
    bio: "Estratega de Marcas y Negocios, Coach de comunicación persuasiva.",
    job: "Conduce las sesiones 1:1 sobre identidad, visión y las decisiones que implican soltar.",
    description: "Con más de 16 años de trayectoria, Rita ha transformado la comunicación de líderes y organizaciones en activos de alto valor percibido.",
    highlight: "Creadora del método Sincronía Persuasiva, diseñado para alinear la identidad profunda del líder con la visión y estrategia comercial del negocio. Especialista en Marca Personal para Directivos, Rita entrena a líderes para que su comunicación deje de ser operativa y pase a ser una herramienta de influencia global y poder de negociación.",
    terms: ["Sincronía Persuasiva", "Marca Personal para Directivos"],
    linkedin: "https://www.linkedin.com/in/ritagalmarini/",
    instagram: "https://www.instagram.com/ritagalmarini"
  },
  {
    name: "Juan Pablo Barrios",
    role: "Co-fundador · Dirección estratégica",
    image: juanPabloImg,
    bio: "Estratega de Marcas y Consultor de Negocios.",
    job: "Conduce territorio, posicionamiento, arquitectura de la oferta y narrativa.",
    description: "Especialista en Branding y construcción de arquitectura de marca estratégica, Juan Pablo ha liderado proyectos transformando marcas en íconos de prestigio.",
    highlight: "Su enfoque combina narrativas de alto valor y ecosistemas evolutivos que maximizan la reputación en mercados competitivos. Su pasión por \"artificar\" marcas eleva los elementos visuales y conceptuales a herramientas clave de diferenciación comercial, transformando la identidad en un activo estratégico.",
    terms: ["narrativas de alto valor", "\"artificar\" marcas", "activo estratégico", "íconos de prestigio"],
    linkedin: "https://www.linkedin.com/in/juanpablobarrios/",
    instagram: "https://www.instagram.com/jp_barrios/"
  }
];

const renderHighlight = (f: (typeof founders)[number]) =>
  f.highlight.split(new RegExp(`(${f.terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g")).map((part, i) =>
    (f.terms as readonly string[]).includes(part)
      ? <strong key={i}>{part}</strong>
      : part
  );

export function MarTeam() {
  return <>
    <section className="band day" id="equipo" data-surface="paper"><div className="wrap">
      <div className="head"><p className="eyebrow">11 · Quiénes te acompañan</p><h2>Hace más de trece años que construimos marcas</h2><p className="lede">Trabajamos con fundadoras y fundadores cuya marca personal y cuyo negocio son la misma cosa, y entramos para dejar una estructura que siga funcionando cuando nos vamos.</p></div>
      <div className="cols cols-2 founders">
        {founders.map(f => (
          <div className="col founder" key={f.name}>
            <img className="founder-photo" src={f.image} alt={f.name} />
            <div className="founder-body">
              <h3>{f.name}</h3>
              <p className="founder-role">{f.role}</p>
              <p className="founder-job"><strong>{f.bio}</strong> {f.job}</p>
              <p className="founder-desc">{f.description}</p>
              <p className="founder-highlight">{renderHighlight(f)}</p>
              <div className="founder-social">
                <a href={f.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`LinkedIn de ${f.name}`}><Linkedin /></a>
                <a href={f.instagram} target="_blank" rel="noopener noreferrer" aria-label={`Instagram de ${f.name}`}><Instagram /></a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div></section>
  </>;
}

export default function MarInvestment() {
  return <>
    <section className="band grad" id="inversion"><div className="wrap">
      <div className="head"><p className="eyebrow">13 · Inversión</p><h2>El mismo alcance, dos formas de pago</h2></div>
      <div className="money">
        <div className="fig"><p className="tag">Valor de lista · en 2 pagos</p><p className="amount"><small>USD</small>{formatUsd(marTerms.listUsd)}</p><p>50% al confirmar y 50% al iniciar la etapa de Dirección.</p></div>
        <div className="fig total"><p className="tag">Pago único · {marDiscountPct}% de beneficio</p><p className="amount"><small>USD</small>{formatUsd(marTerms.singlePaymentUsd)}</p><p>Al confirmar. Incluye el mismo alcance completo.</p></div>
      </div>
      <p className="fine">Valores en dólares estadounidenses, por transferencia internacional. Propuesta válida hasta el {marTerms.validUntil}.</p>
    </div></section>

    <section className="band day" id="limites" data-surface="paper"><div className="wrap">
      <div className="head"><p className="eyebrow">14 · Los límites del trabajo</p><h2>Lo que incluye esta etapa, y lo que viene después</h2></div>
      <div className="split">
        <div className="criteria"><p className="eyebrow" style={{ paddingBottom: "16px", border: "none" }}>No incluye</p>
          <p>Repetir el diagnóstico de negocio que ya iniciado.</p><p>Producción de contenido, community management o pauta.</p><p>Gestión de prensa o contratación de medios.</p><p>Identidad visual, web o diseño de piezas.</p></div>
        <aside className="note"><p className="eyebrow">Si el proceso lo determina</p>
          <p className="big">Lo recomendamos solo si hace falta, con su propia propuesta.</p>
          <p className="muted">La construcción de tu expresión de marca (identidad verbal y visual, nombres de programas, web), revisiones periódicas de criterio con tu equipo y el contacto de una especialista en PR que multiplica cada aparición en medios.</p></aside>
      </div>
    </div></section>

    <section className="band quote" id="cierre">
      <figure className="wrap">
        <p className="q-mark" aria-hidden="true">“</p>
        <blockquote>
          “Ya probaste que tu marca funciona.
          <br />Ahora hagamos que perdure.”
        </blockquote>
        <figcaption>Coressen Capital™ · Founder</figcaption>
      </figure>
    </section>

    <footer className="band night" id="primer-paso"><div className="wrap">
      <div className="sign"><p className="tag">Estrategia con alma. Branding con visión.</p><a className="who" href="https://coressenglobal.com" target="_blank" rel="noopener noreferrer">Coressen · coressenglobal.com</a><Button variant="ghost" size="icon" asChild><a href="#inicio" aria-label="Volver al inicio"><ArrowUp /></a></Button></div>
    </div></footer>
  </>;
}
